import React, { createContext, useState, useContext, useRef, ReactNode, FC, useMemo, useEffect } from 'react';

import { Button, makeStyles, Typography } from '@material-ui/core';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import DialogGrid from 'components/_dialogs/_components/DialogGrid';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormTextInput from 'components/FormTextInput';
import otp_messages from 'messages/otp_messages';
import validation_messages from 'messages/validation_messages';

const useStyles = makeStyles(theme => ({
  button: {
    // @ts-ignore
    minWidth: theme.sizes.button.narrow,
  },
  wrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(3),
    width: '100%',
  },
  deviceList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    width: '100%',
  },
}));

type FulfilledResult = {
  otpToken: string;
};

export type OTPAuthDevice = {
  id: number;
  name: string;
  default: boolean;
};

type OTPAuthModalContextType = {
  showOTPAuthModal: (devices?: OTPAuthDevice[]) => Promise<null | FulfilledResult>;
  closeOTPAuthModal: () => void;
};

const OTPAuthModalContext = createContext({} as OTPAuthModalContextType);

const { Provider } = OTPAuthModalContext;

type Props = {
  children: ReactNode;
};

type FormValues = {
  otpToken: string;
};

const FORM_ID = 'OTPAuthModalProviderForm';

const OTPAuthModalProvider: FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<OTPAuthDevice[]>([]);
  // const [modalOptions, setModalOptions] = useState<ModalOptions | null>(null);
  const awaitingConfirmation: React.MutableRefObject<{ resolve: (result: null | FulfilledResult) => void } | undefined> = useRef(undefined);

  const closeModal = () => {
    setOpen(false);
    setDevices([]);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    formik.resetForm();
  };

  const onSubmit = (formData: FormValues) => {
    if (awaitingConfirmation.current) {
      awaitingConfirmation.current.resolve({ otpToken: formData.otpToken });
    }

    closeModal();
  };

  const showOTPAuthModal = (availableDevices: OTPAuthDevice[] = []) => {
    setDevices(availableDevices);
    setOpen(true);
    return new Promise(resolve => {
      awaitingConfirmation.current = { resolve };
    });
  };

  const onCancel = () => {
    if (awaitingConfirmation.current) {
      awaitingConfirmation.current.resolve(null);
    }

    closeModal();
  };

  const formik = useFormik({
    initialValues: {
      otpToken: '',
    },
    validationSchema: yup.object({
      otpToken: yup.string().required(t(validation_messages.required)),
    }),
    enableReinitialize: true,
    onSubmit,
  });

  const tokenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    tokenInputRef.current?.focus();
  }, [open]);

  const value = useMemo(() => ({ showOTPAuthModal, closeOTPAuthModal: closeModal }), []);

  const styles = useStyles();
  return (
    <Provider value={value as OTPAuthModalContextType}>
      {children}
      {/* @ts-ignore */}
      <DialogGrid
        dialogActions={
          <div className={styles.wrapper}>
            <Button className={styles.button} onClick={onCancel} variant='outlined'>
              {t(otp_messages.otp_auth_dialog.cancel_button_label)}
            </Button>
            {/* @ts-ignore */}
            <ColoredButton className={styles.button} customColor='secondary' form={FORM_ID} type='submit' variant='outlined'>
              {t(otp_messages.otp_auth_dialog.confirm_button_label)}
            </ColoredButton>
          </div>
        }
        maxWidth='xs'
        onClose={onCancel}
        open={open}
        title={t(otp_messages.otp_auth_dialog.title)}
      >
        <form id={FORM_ID} onSubmit={formik.handleSubmit}>
          <CenteredGrid gridGap={2} tight>
            <Typography>{t(otp_messages.otp_auth_dialog.description_any_device)}</Typography>
            {devices.length > 0 && (
              <ul className={styles.deviceList}>
                {devices.map(device => (
                  <li key={device.id}>
                    <Typography component='span'>{device.name}</Typography>
                  </li>
                ))}
              </ul>
            )}
            <FormTextInput
              formik={formik}
              id='otpToken'
              inputProps={{ autoComplete: 'one-time-code' }}
              inputRef={tokenInputRef}
              label={t(otp_messages.otp_auth_dialog.auth_code_label)}
            />
          </CenteredGrid>
        </form>
      </DialogGrid>
    </Provider>
  );
};

export const useOTPAuthModalContext = () => useContext(OTPAuthModalContext);

export default OTPAuthModalProvider;
